import React, {Suspense, useEffect, useMemo, useState} from 'react';
import PageTemplate from 'Components/Templates/Page/Page/PageTemplate';
import {Helmet} from 'react-helmet-async';
import {ROUTE} from 'Services/Route/Route.types';
import PageHeader from 'Components/Organisms/PageHeader/PageHeader';
import Breadcrumbs from 'Components/Molecules/Breadcrumbs/Breadcrumbs';
import BreadcrumbItem from 'Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import OverviewHeader from 'Components/Molecules/Headers/OverviewHeader/OverviewHeader';
import StatusLabel from 'Components/Atoms/Label/StatusLabel/StatusLabel';
import {TableProvider} from 'Services/Table/TableContext';
import TableColumn from 'Components/Molecules/TableColumn/TableColumn';
import Container from 'Components/Atoms/Container/Container';
import TableFixed from 'Components/Molecules/Table/TableFixed/TableFixed';
import TableHeader from 'Components/Molecules/TableHeader/TableHeader';
import TableBody from 'Components/Molecules/TableBody/TableBody';
import TableCell from 'Components/Molecules/TableCell/TableCell';
import TableRow from 'Components/Molecules/TableRow/TableRow';
import {convertTimestampToDateAndTime} from 'Services/Date/Date';
import useFetch from 'Api/useFetch';
import Modal from 'Components/Molecules/Modals/Modal/Modal';
import ModalHeader from 'Components/Molecules/Modals/ModalHeader/ModalHeader';
import Button from 'Components/Atoms/Button/Button';
import FontAwesomeIcon from 'Components/Atoms/FontAwesomeIcon/FontAwesomeIcon';
import {faFilter, faXmark} from '@fortawesome/pro-regular-svg-icons';
import Title from 'Components/Atoms/Title/Title';
import Mail from 'Components/Organisms/Modules/Mails/Mail/Mail';
import {faMagnifyingGlass} from '@fortawesome/pro-light-svg-icons';
import TextField from 'Components/Atoms/Fields/TextField/TextField';
import Loader from 'Components/Atoms/Loader/Loader';
import Dropdown from 'Components/Atoms/Dropdown/Dropdown';
import PageLoader from 'Components/Atoms/Loader/PageLoader/PageLoader';
import TableLoader from 'Components/Atoms/Loader/TableLoader/TableLoader';
import ActiveFilterBlocks from 'Components/Molecules/Filters/ActiveFilterBlocks/ActiveFilterBlocks';

const MailsFilters = React.lazy(() => import('Components/Organisms/Modules/Mails/MailsFilters/MailsFilters'));

const MailsPage = () => {
    const pageSize = 20;
    const [metadata, setMetadata] = useState({
        total: 0,
        total_filtered: 0,
        status_imported: 0,
        status_connected: 0
    });
    const [state, setState] = useState<any>({
        isPageLoading: true,
        viewMailData: {},

        searchQuery: '',
        currentPage: 1
    });
    const [searchTimer, setSearchTimer] = useState<any>(null);
    const [filters, setFilters] = useState<any>({});
    const activeFilterKeys = useMemo(() =>
            Object.keys(filters)
                ?.filter(i => !['query', 'type'].includes(i))
        , [filters]);

    const requestParams = useMemo<any>(() => {
        let params: any = {
            limit: pageSize,
            offset: 0,
            "order[sent_at]": 'DESC',
        }

        if (state.currentPage !== 0) params.offset = (state.currentPage - 1) * pageSize;
        if (filters) {
            const filterRequest = {};
            for (const key in filters) {
                const newKey = `filters[${key}]`;
                filterRequest[newKey] = filters[key];
            }

            params = {...params, ...filterRequest};
        };

        return params;
    }, [state.currentPage, filters]);

    const {
        isLoading,
        isError,
        data,
        isFetching,
        refetch
    } = useFetch(
        '/api/v2.9/mails',
        requestParams,
        {
            select: (data) => data?.data,
            onSuccess: (data) => {
                setState((state) => ({
                    ...state,
                    isPageLoading: false
                }));
                setMetadata(data?.metadata);
            }
        }
    );

    const handlePageChange = (newPageNumber: number) => setState((state) => ({
        ...state,
        currentPage: newPageNumber
    }))

    const handleMailClick = (i) => () => setState((state) => ({
        ...state,
        viewMailData: i
    }));

    const handleRemoveFilter = (i: any) => {
        const newFilters = {...filters};
        delete newFilters[i];

        setFilters(newFilters);
    }

    const handleSearchQuery = (value: string) => {
        if (value.trim() == state.searchQuery.trim()) return;

        clearTimeout(searchTimer);
        setSearchTimer(null);

        setState((state) => ({
            ...state,
            searchQuery: value
        }));

        setSearchTimer(
            setTimeout(() => {
                let newFilters = { ...filters };
                if (value) {
                    newFilters = {
                        ...newFilters,
                        query: value
                    };
                } else {
                    delete newFilters['query'];

                    newFilters = { ...newFilters };
                }

                setFilters(newFilters);
            }, 500)
        );
    };

    return <PageTemplate>
        <Helmet>
            <title>Mails</title>
        </Helmet>

        { state.isPageLoading && <PageLoader description={'Mails laden...'} /> }

        {
            !state.isPageLoading && <>
                <PageHeader
                    breadcrumbs={
                        <Breadcrumbs>
                            <BreadcrumbItem path={ROUTE.DASHBOARD}>Home</BreadcrumbItem>
                            <BreadcrumbItem>Mails</BreadcrumbItem>
                        </Breadcrumbs>
                    }
                >
                    <OverviewHeader
                        icon={'mail'}
                        title={`${metadata?.total ?? 0} Mails`}
                    >
                        <StatusLabel status='active'>
                            {metadata?.status_connected ?? 0} gekoppeld
                        </StatusLabel>
                        <StatusLabel status='passive'>
                            {metadata?.status_imported ?? 0} geimporteerd
                        </StatusLabel>
                    </OverviewHeader>
                </PageHeader>

                <Container padding={'vertical'}>
                    <TableProvider
                        pageNumber={1}
                        totalPageNumber={Math.ceil(data?.metadata?.total / pageSize)}
                        onPageChange={handlePageChange}
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px'
                        }}>
                            <div style={{
                                display: 'flex',
                                gap: '10px',
                                maxWidth: '550px'
                            }}>
                                <TextField
                                    iconBefore={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                                    size='large'
                                    placeholder={`Zoeken op onderwerp, verzender, ontvanger of cc...`}
                                    disableBlur

                                    value={state.searchQuery}
                                    onChange={handleSearchQuery}
                                />
                                <Dropdown
                                    disableCloseOnOutsideClick
                                    disableCloseOnClick
                                    triggerAction='click'
                                    trigger={
                                        <Button
                                            variant='transparent'
                                            color='primary'
                                            size='large'
                                            iconBefore={<FontAwesomeIcon icon={faFilter} />}
                                        >
                                            Filters beheren { activeFilterKeys.length > 0 && `(${activeFilterKeys.length})` }
                                        </Button>
                                    }
                                >
                                    <Suspense
                                        fallback={
                                            <Loader />
                                        }
                                    >
                                        <MailsFilters
                                            filters={filters}
                                            onFilterChange={setFilters}
                                        />
                                    </Suspense>
                                </Dropdown>
                            </div>

                            <ActiveFilterBlocks
                                statusMapper={{
                                    'CONNECTED': 'Gekoppeld',
                                    'IMPORTED': 'Geimporteerd'
                                }}
                                metadata={metadata}
                                filters={filters}
                                onDelete={handleRemoveFilter}
                            />

                            <TableFixed
                                header={
                                    <TableHeader>
                                        <TableColumn
                                            position='fixed'
                                            id='status'
                                        >
                                            Status
                                        </TableColumn>
                                        <TableColumn
                                            position='fixed'
                                            size='large'
                                            id='name'
                                            primary
                                        >
                                            Onderwerp
                                        </TableColumn>
                                        <TableColumn size='large'>
                                            Verzender
                                        </TableColumn>
                                        <TableColumn size='large'>
                                            Ontvanger
                                        </TableColumn>
                                        <TableColumn size='large'>
                                            CC
                                        </TableColumn>
                                        <TableColumn>
                                            Datum
                                        </TableColumn>
                                    </TableHeader>
                                }
                            >
                                <TableBody>
                                    {
                                        isLoading
                                            ? <TableLoader />
                                            : data?.mails?.map(i => <TableRow href={''} onClick={handleMailClick(i)}>
                                                <TableCell>
                                                    <StatusLabel status={
                                                        i?.status === 'CONNECTED' ? 'active' : 'passive'
                                                    }>
                                                        { i?.status === 'CONNECTED' ? 'Gekoppeld' : 'Geimporteerd' }
                                                    </StatusLabel>
                                                </TableCell>
                                                <TableCell size='large'>
                                                    { i?.subject }
                                                </TableCell>
                                                <TableCell size='large'>
                                                    {
                                                        i?.sender?.value?.map((i, idx: number, arr: any[]) => <>
                                                            { i?.name } &lt;{ i?.email }&gt; {idx !== arr.length-1 ? ', ' : ''}
                                                        </>)
                                                    }
                                                </TableCell>
                                                <TableCell size='large'>
                                                    {
                                                        i?.receivers?.value?.map((i, idx: number, arr: any[]) => <>
                                                            { i?.name } &lt;{ i?.email }&gt; {idx !== arr.length-1 ? ', ' : ''}
                                                        </>)
                                                    }
                                                </TableCell>
                                                <TableCell size='large'>
                                                    {
                                                        i?.cc?.value?.map((i, idx: number, arr: any[]) => <>
                                                            { i?.name } &lt;{ i?.email }&gt; {idx !== arr.length-1 ? ', ' : ''}
                                                        </>) ?? '-'
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    { convertTimestampToDateAndTime(i?.sent_at) }
                                                </TableCell>
                                            </TableRow>)
                                    }
                                </TableBody>
                            </TableFixed>
                        </div>
                    </TableProvider>
                </Container>
            </>
        }
        {
            state.viewMailData?.subject &&
            <Modal>
                <ModalHeader actions={
                    <Button
                        iconBefore={<FontAwesomeIcon icon={faXmark} />}
                        onClick={handleMailClick({})}
                    />
                }>
                    <Title size='large' weight='bold'>{ state.viewMailData?.subject }</Title>
                </ModalHeader>
                <Container padding={'vertical'}>
                    <Mail
                        data={state.viewMailData}
                        onDelete={() => {
                            handleMailClick({})();
                            refetch();
                        }}
                    />
                </Container>
            </Modal>
        }
    </PageTemplate>
}

export default MailsPage;